import React, { useState, useMemo } from 'react';
import JobCard from '../components/JobCard';
import PageLayout from '../components/PageLayout';
import { Zap, MapPin, DollarSign, Globe, Briefcase, RotateCcw } from 'lucide-react';

const JobsListing = ({ jobsData }) => {
  // --- STATES ---
  const [selectedCity, setSelectedCity] = useState('All');
  const [minSalary, setMinSalary] = useState(0);
  const [isRemoteOnly, setIsRemoteOnly] = useState(false);
  const [employmentType, setEmploymentType] = useState('All');

  // --- FILTERING LOGIC ---
  const filteredJobs = useMemo(() => {
    // 1. Initial City Flattening
    let list = selectedCity === 'All' 
      ? Object.values(jobsData).flat() 
      : jobsData[selectedCity] || [];

    // 2. Remote Filter (Using normalized boolean)
    if (isRemoteOnly) {
      list = list.filter(job => job.job_is_remote === true);
    }

    // 3. Employment Type Filter (Using normalized array)
    if (employmentType !== 'All') {
      list = list.filter(job => 
        job.job_employment_types && job.job_employment_types.includes(employmentType)
      );
    }

    // 4. Salary Filter (Using normalized number)
    if (minSalary > 0) {
      list = list.filter(job => job.job_min_salary >= minSalary);
    }

    // 5. Sort by Newest (Timestamp)
    return [...list].sort((a, b) => b.timestamp - a.timestamp);
  }, [jobsData, selectedCity, minSalary, isRemoteOnly, employmentType]);

  // --- UI HELPERS ---
  const cities = ['All', ...Object.keys(jobsData)];
  const empTypes = [
    { label: 'All Types', value: 'All' },
    { label: 'Full Time', value: 'FULLTIME' },
    { label: 'Part Time', value: 'PARTTIME' },
    { label: 'Contract', value: 'CONTRACTOR' },
    { label: 'Internship', value: 'INTERN' }
  ];

  const resetFilters = () => {
    setSelectedCity('All');
    setMinSalary(0);
    setIsRemoteOnly(false);
    setEmploymentType('All');
  };

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 font-sans text-slate-900">
        
        {/* HERO SECTION */}
        <div className="py-8">
          <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-600 to-indigo-500"></div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-2">
                  Today's <span className="text-blue-600">AI Roles</span>
                </h1>
                <p className="text-slate-500 max-w-md font-medium">
                  Curated Agentic & Full-stack opportunities for the Mindslog community.
                </p>
              </div>
              <div className="flex items-center gap-4 bg-blue-50 px-6 py-4 rounded-3xl border border-blue-100">
                <Zap className="text-blue-600" fill="currentColor" size={24} />
                <div>
                  <p className="text-[10px] font-black uppercase text-blue-600 tracking-widest">Live Updates</p>
                  <p className="text-sm font-bold text-slate-800">{filteredJobs.length} Positions Found</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="mb-10 space-y-6">
          {/* 1. Cities */}
          <div className="flex flex-col gap-3">
            <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-2">
              <MapPin size={12} /> Filter by Location
            </span>
            <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
              {cities.map(city => (
                <button
                  key={city}
                  onClick={() => setSelectedCity(city)}
                  className={`px-6 py-2 rounded-2xl text-xs font-bold transition-all border ${
                    selectedCity === city 
                    ? 'bg-slate-900 border-slate-900 text-white shadow-lg' 
                    : 'bg-white border-slate-200 text-slate-500 hover:border-blue-300'
                  }`}
                >
                  {city}
                </button>
              ))}
            </div>
          </div>

          {/* 2. Advanced Filters */}
          <div className="flex flex-col lg:flex-row gap-6 items-end">
            <div className="flex flex-wrap gap-4 flex-1">
              {/* Remote Toggle */}
              <button
                onClick={() => setIsRemoteOnly(!isRemoteOnly)}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-xs font-bold border transition-all ${
                  isRemoteOnly 
                  ? 'bg-blue-600 border-blue-600 text-white shadow-md' 
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Globe size={16} /> Remote Only
              </button>

              {/* Employment Type */}
              <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm">
                <Briefcase size={16} className="text-slate-400" />
                <select 
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                  className="text-xs font-bold text-slate-700 outline-none bg-transparent cursor-pointer"
                >
                  {empTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Salary Slider */}
            <div className="w-full lg:w-80 bg-white p-5 rounded-3xl border border-slate-200 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-2">
                  <DollarSign size={12} /> Min Salary
                </span>
                <span className="text-blue-600 font-black text-sm">₹{(minSalary/1000).toFixed(0)}k+</span>
              </div>
              <input 
                type="range" min="0" max="150000" step="10000"
                value={minSalary}
                onChange={(e) => setMinSalary(Number(e.target.value))}
                className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          </div>
        </div>

        {/* JOB LISTING GRID */}
        <div className="grid gap-4">
          {filteredJobs.length > 0 ? (
            filteredJobs.map((job, idx) => (
              <JobCard key={job.id || idx} job={job} />
            ))
          ) : (
            <div className="py-32 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200">
              <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <RotateCcw className="text-slate-300" />
              </div>
              <h3 className="text-lg font-bold text-slate-800">No matches found</h3>
              <p className="text-slate-400 text-sm mb-6">Try adjusting your filters or search criteria.</p>
              <button 
                onClick={resetFilters}
                className="px-8 py-3 bg-blue-600 text-white rounded-2xl text-xs font-bold hover:shadow-xl transition-all"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default JobsListing;